import { useNetworkDashboardKpis, useRealEstateKpis, useStoreProjects } from '@gozoki/api'
import {
  NETWORK_DASHBOARD_STORE_AGE_FILTERS,
  type NetworkDashboardDailyAggregate,
} from '@gozoki/api-types'
import { createFileRoute } from '@tanstack/react-router'
import { type JSX, useState } from 'react'
import { z } from 'zod'
import { OpenStoresCountCardLink } from '~/components/dashboards/open-stores-count-card'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import { getPercentageOfAnnualCa } from '~/components/routes/development/real-estate/map/map-dashboard-stats'
import {
  computeAverageLandCost,
  computeAverageRent,
} from '~/components/routes/development/real-estate/map/map-kpis'
import type { RealEstateProject } from '~/components/routes/development/real-estate/store-project/types'
import { CACard } from '~/components/routes/network-dashboard/cards/ca-card'
import { CADetailsCard } from '~/components/routes/network-dashboard/cards/ca-details-card'
import { CategoriesCard } from '~/components/routes/network-dashboard/cards/categories-card'
import { CustomerSatifsactionCard } from '~/components/routes/network-dashboard/cards/customer-satifsaction-card'
import { HRCard } from '~/components/routes/network-dashboard/cards/hr-card'
import { NetworkStatCard } from '~/components/routes/network-dashboard/cards/network-stat-card'
import { ProductsCard } from '~/components/routes/network-dashboard/cards/products-card'
import { RealEstateCostsCard } from '~/components/routes/network-dashboard/cards/real-estate-costs-card'
import { UpcomingStoresCard } from '~/components/routes/network-dashboard/cards/upcoming-stores-card'
import { NetworkFilters } from '~/components/routes/network-dashboard/network-filters'
import { Button } from '~/components/ui/button'
import { getUpcomingStores } from '~/utils/new-dashboards/upcoming-stores'

function getAggregateValue(
  aggregate: NetworkDashboardDailyAggregate,
  showMatureOnly: boolean,
  matureKey:
    | 'matureCa'
    | 'matureCartsCount'
    | 'matureProductsSold'
    | 'matureCaHt'
    | 'matureStolenBuyingValue'
    | 'matureStolenCount'
    | 'matureDestroyedBuyingValue'
    | 'matureDestroyedCount',
  totalKey:
    | 'totalCa'
    | 'totalCartsCount'
    | 'totalProductsSold'
    | 'totalCaHt'
    | 'totalStolenBuyingValue'
    | 'totalStolenCount'
    | 'totalDestroyedBuyingValue'
    | 'totalDestroyedCount',
): number {
  if (showMatureOnly) {
    return aggregate[matureKey]
  }

  return aggregate[totalKey]
}

function NetworkDashboardPage(): JSX.Element {
  const { storeIds, storeAgeFilter } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { data: projects = [] } = useStoreProjects()
  const { data: realEstateKpis } = useRealEstateKpis()
  const { data: networkKpis } = useNetworkDashboardKpis(storeIds, storeAgeFilter)

  const [isModalOpen, setIsModalOpen] = useState(false)

  // Two effective "show > 3 mois" flags driven by the seniority filter:
  //  - `networkShowMatureOnly`: used for cards whose historical default was
  //    to aggregate over all stores (CA total, CA temps réel). They only
  //    flip to the mature subset when the user explicitly picks `mature`.
  //  - `matureDefaultShowMatureOnly`: used for cards whose historical
  //    default was to aggregate over mature stores only (store CA, HR,
  //    network stat cards). They stay on the mature subset both for
  //    `default` and `mature`, and only switch to every store for `all`.
  const networkShowMatureOnly = storeAgeFilter === 'mature'
  const matureDefaultShowMatureOnly = storeAgeFilter !== 'all'

  const dailyAggregates = networkKpis?.dailyAggregates ?? []
  const storeWeeklyCas = networkKpis?.storeWeeklyCas ?? []
  const categoryPdm = networkKpis?.categoryPdm ?? []
  const topProducts = networkKpis?.topProducts ?? []

  const midpoint = Math.ceil(dailyAggregates.length / 2)
  const currentWeekAggregates = dailyAggregates.slice(midpoint)
  const previousWeekAggregates = dailyAggregates.slice(0, midpoint)
  const totalCaTTC = currentWeekAggregates.reduce((sum, a) => sum + a.totalCa, 0)
  const previousCaTTC = previousWeekAggregates.reduce((sum, a) => sum + a.totalCa, 0)
  const caTTCPercentageChange =
    previousCaTTC === 0 ? 0 : Math.round(((totalCaTTC - previousCaTTC) / previousCaTTC) * 100)

  // The following block feeds the cards whose pre-filter default was mature
  // only (store CA, HR, stat cards). `matureDefaultShowMatureOnly` is true
  // when the override is `default` or `mature`, false only when `all`.
  const currentCaTTC = currentWeekAggregates.reduce(
    (sum, a) => sum + getAggregateValue(a, matureDefaultShowMatureOnly, 'matureCa', 'totalCa'),
    0,
  )
  const previousCaTTCSelected = previousWeekAggregates.reduce(
    (sum, a) => sum + getAggregateValue(a, matureDefaultShowMatureOnly, 'matureCa', 'totalCa'),
    0,
  )
  const currentCartsCount = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(a, matureDefaultShowMatureOnly, 'matureCartsCount', 'totalCartsCount'),
    0,
  )
  const previousCartsCount = previousWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(a, matureDefaultShowMatureOnly, 'matureCartsCount', 'totalCartsCount'),
    0,
  )
  const currentProductsSold = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(a, matureDefaultShowMatureOnly, 'matureProductsSold', 'totalProductsSold'),
    0,
  )

  const currentCAPerStore = currentWeekAggregates.reduce((sum, a) => {
    const storeCount = matureDefaultShowMatureOnly ? a.matureStoreCount : a.storeCount
    const caValue = matureDefaultShowMatureOnly ? a.matureCa : a.totalCa
    if (storeCount <= 0) return sum
    return sum + caValue / storeCount
  }, 0)

  const hrMetrics = networkKpis?.hrMetrics
  const hrPercentageCA =
    hrMetrics && currentCAPerStore > 0
      ? Math.round((hrMetrics.avgSalaryCostPerStore / currentCAPerStore) * 100)
      : 0

  const panierMoyen = currentCartsCount === 0 ? 0 : currentCaTTC / currentCartsCount
  const previousPanierMoyen =
    previousCartsCount === 0 ? 0 : previousCaTTCSelected / previousCartsCount
  const panierMoyenPercentageChange =
    previousPanierMoyen === 0
      ? 0
      : Math.round(((panierMoyen - previousPanierMoyen) / previousPanierMoyen) * 100)

  const articlesPerCart = currentCartsCount === 0 ? 0 : currentProductsSold / currentCartsCount

  const currentCaHT = currentWeekAggregates.reduce(
    (sum, a) => sum + getAggregateValue(a, matureDefaultShowMatureOnly, 'matureCaHt', 'totalCaHt'),
    0,
  )
  const previousCaHT = previousWeekAggregates.reduce(
    (sum, a) => sum + getAggregateValue(a, matureDefaultShowMatureOnly, 'matureCaHt', 'totalCaHt'),
    0,
  )

  const currentStolenValue = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureStolenBuyingValue',
        'totalStolenBuyingValue',
      ),
    0,
  )
  const previousStolenValue = previousWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureStolenBuyingValue',
        'totalStolenBuyingValue',
      ),
    0,
  )
  const currentStolenCount = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(a, matureDefaultShowMatureOnly, 'matureStolenCount', 'totalStolenCount'),
    0,
  )
  const txVol = currentCaHT > 0 ? (currentStolenValue / currentCaHT) * 100 : 0
  const previousTxVol = previousCaHT > 0 ? (previousStolenValue / previousCaHT) * 100 : 0
  const txVolChange =
    previousTxVol === 0 ? 0 : Math.round(((txVol - previousTxVol) / previousTxVol) * 100)

  const currentDestroyedValue = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureDestroyedBuyingValue',
        'totalDestroyedBuyingValue',
      ),
    0,
  )
  const previousDestroyedValue = previousWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureDestroyedBuyingValue',
        'totalDestroyedBuyingValue',
      ),
    0,
  )
  const currentDestroyedCount = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureDestroyedCount',
        'totalDestroyedCount',
      ),
    0,
  )
  const txCasse = currentCaHT > 0 ? (currentDestroyedValue / currentCaHT) * 100 : 0
  const previousTxCasse = previousCaHT > 0 ? (previousDestroyedValue / previousCaHT) * 100 : 0
  const txCasseChange =
    previousTxCasse === 0 ? 0 : Math.round(((txCasse - previousTxCasse) / previousTxCasse) * 100)

  const delta = realEstateKpis?.openStoresDelta30d ?? 0
  const openStoresDeltaIndex = `${delta >= 0 ? '+' : ''}${delta} vs M-1`
  const typedProjects = projects as RealEstateProject[]
  const openStoresCount = typedProjects.filter((p) => p.status === 'open' && !p.isAbandoned).length
  const today = new Date()

  const upcomingStores = getUpcomingStores(typedProjects).filter(
    (p) =>
      today <= p.openingDate &&
      p.openingDate <= new Date(today.getTime() + 3 * 3_600_000 * 24 * 30),
  )

  const avgLandCostEur = realEstateKpis?.avgLandCostEur ?? computeAverageLandCost(typedProjects)
  const avgRent = computeAverageRent(typedProjects)
  const avgLandCostPctOfCa = getPercentageOfAnnualCa(
    avgLandCostEur,
    realEstateKpis?.averageAnnualCaEur ?? 0,
  )
  const avgRentPctOfCa = getPercentageOfAnnualCa(avgRent, realEstateKpis?.averageAnnualCaEur ?? 0)

  return (
    <>
      <AppHeader className="mb-4">
        <Title className="text-foreground text-[32px] font-extrabold leading-[normal] font-brand">
          Réseau & Boutiques
        </Title>
      </AppHeader>

      <AppContainer>
        <div className="flex justify-end">
          <Button variant="outline" className="w-fit" onClick={() => setIsModalOpen(true)}>
            Sélecteur
          </Button>
        </div>

        {isModalOpen && (
          <NetworkFilters
            open={isModalOpen}
            onOpenChange={setIsModalOpen}
            selectedStoreIds={storeIds}
            selectedStoreAgeFilter={storeAgeFilter}
            availableStores={networkKpis?.availableStores ?? []}
            onApply={(newStoreIds, newStoreAgeFilter) => {
              void navigate({
                search: {
                  storeIds: newStoreIds.length > 0 ? newStoreIds : undefined,
                  storeAgeFilter: newStoreAgeFilter === 'default' ? undefined : newStoreAgeFilter,
                },
              })
              setIsModalOpen(false)
            }}
          />
        )}
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: '16rem minmax(0, 1fr) minmax(10rem, 15rem)' }}
        >
          {/* Left column */}
          <div className="max-h-0 min-h-full flex flex-col gap-2">
            <OpenStoresCountCardLink
              openStoresCount={openStoresCount}
              openStoresDeltaIndex={openStoresDeltaIndex}
            />
            <UpcomingStoresCard upcomingStores={upcomingStores} />
            <RealEstateCostsCard
              avgLandCostEur={avgLandCostEur ?? 0}
              avgLandCostPctOfCa={avgLandCostPctOfCa ?? ''}
              avgRent={avgRent ?? 0}
              avgRentPctOfCa={avgRentPctOfCa ?? ''}
            />
            <CustomerSatifsactionCard customerSatifsaction={4.6} />
          </div>

          {/* Center column */}
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex min-w-0 gap-2">
              <div className="flex-1 min-w-0">
                <CACard
                  variant="network"
                  aggregates={dailyAggregates}
                  showMatureOnly={networkShowMatureOnly}
                  isStoreSelectionActive={storeIds.length > 0}
                />
              </div>
              <div className="flex-1 min-w-0">
                <CACard
                  variant="store"
                  aggregates={dailyAggregates}
                  showMatureOnly={matureDefaultShowMatureOnly}
                />
              </div>
            </div>
            <div className="flex min-w-0 gap-2">
              <div className="flex-[1.2] min-w-0">
                <CategoriesCard categories={categoryPdm} showMatureOnly={networkShowMatureOnly} />
              </div>
              <div className="flex-[0.8] min-w-0">
                <ProductsCard
                  products={topProducts}
                  categories={categoryPdm}
                  showMatureOnly={networkShowMatureOnly}
                />
              </div>
            </div>
            <div className="flex min-w-0 gap-2">
              <div className="flex-[0.4] flex">
                <HRCard
                  avgETP={hrMetrics?.avgETP ?? 0}
                  percentageCA={hrPercentageCA}
                  percentageInPerson={hrMetrics?.inPersonHoursTotal ?? 0}
                  percentageRemote={hrMetrics?.remoteHoursTotal ?? 0}
                  showMatureOnly={matureDefaultShowMatureOnly}
                />
              </div>
              <div className="flex-1 min-w-0 grid grid-cols-3 gap-2">
                <NetworkStatCard
                  title="Tx de casse"
                  value={`${txCasse.toFixed(1)}%`}
                  subText={`${Math.round(currentDestroyedCount)} UVC`}
                  comparisonPercentage={txCasseChange}
                  desiredTrend="down"
                  comparisonText="vs S-1"
                  showMatureOnly={matureDefaultShowMatureOnly}
                />
                <NetworkStatCard
                  title="Tx de vol"
                  value={`${txVol.toFixed(1)}%`}
                  subText={`${Math.round(currentStolenCount)} UVC`}
                  comparisonPercentage={txVolChange}
                  desiredTrend="down"
                  comparisonText="vs S-1"
                  showMatureOnly={matureDefaultShowMatureOnly}
                />
                <NetworkStatCard
                  title="Autres charges"
                  value="10%"
                  subText="10 UVC"
                  comparisonPercentage={10}
                  desiredTrend="down"
                  comparisonText="vs S-1"
                  showMatureOnly={matureDefaultShowMatureOnly}
                />
                <NetworkStatCard
                  title="Tx de service"
                  value="10%"
                  subText="10 UVC"
                  comparisonPercentage={10}
                  desiredTrend="up"
                  comparisonText="vs S-1"
                  showMatureOnly={matureDefaultShowMatureOnly}
                />
                <NetworkStatCard
                  title="Panier moyen"
                  value={`${Number(panierMoyen.toFixed(2)).toLocaleString('fr-FR')} €`}
                  subText={`${articlesPerCart.toFixed(1)} A/T`}
                  comparisonPercentage={panierMoyenPercentageChange}
                  desiredTrend="up"
                  comparisonText="vs S-1"
                  showMatureOnly={matureDefaultShowMatureOnly}
                />
                <NetworkStatCard
                  variant="primary-light"
                  title="Contribution"
                  value="10%"
                  subText="10 UVC"
                  comparisonPercentage={10}
                  desiredTrend="up"
                  comparisonText="vs S-1"
                  showMatureOnly={matureDefaultShowMatureOnly}
                />
              </div>
            </div>
          </div>

          {/* Right column — max-h-0 + min-h-full prevents this cell from
             influencing the grid row height while still stretching to fill it */}
          <div className="max-h-0 min-h-full">
            <CADetailsCard
              caTTC={totalCaTTC}
              caTTCPercentageChange={caTTCPercentageChange}
              storeWeeklyCas={storeWeeklyCas}
              showMatureOnly={networkShowMatureOnly}
            />
          </div>
        </div>
      </AppContainer>
    </>
  )
}

const networkDashboardSearchSchema = z.object({
  storeIds: z.array(z.number()).optional().default([]),
  storeAgeFilter: z.enum(NETWORK_DASHBOARD_STORE_AGE_FILTERS).optional().default('default'),
})

export type NetworkDashboardSearch = z.infer<typeof networkDashboardSearchSchema>

export const Route = createFileRoute('/_authenticated/network-dashboard/')({
  component: NetworkDashboardPage,
  validateSearch: networkDashboardSearchSchema,
})
